import { z } from 'zod';
import { wordsIn } from './coach-voice.js';
import { MAX_SOURCE_TITLE } from './common.js';
import { collectionIdSchema, collectionKindSchema, contentStatusSchema, coursePaletteSchema, difficultySchema, illustrationObjectSchema, lessonCastSchema, lessonFormatSchema, HINT_MAX, KEBAB_SLUG, SCENE_BUCKETS_MAX, SCENE_ITEMS_MAX, SLIDER_UNIT_MAX, lessonIdSchema, personaLabelSchema, personaSchema, questionIdSchema, reviewStatusSchema, sceneIdSchema, sceneItemIdSchema, authorableKindSchema, sceneVisualSchema, sourceIdSchema, sourceTypeSchema, subjectSchema, tagSlugSchema, verbSchema, } from './content.js';
import { problemDetailSchema } from './error.js';
import { feedbackTallySchema } from './feedback.js';
import { curriculumLessonIdSchema, generationJobIdSchema, rowTallySchema, } from './generation.js';
import { lessonTermsInputSchema } from './terms.js';
/**
 * What a generator may write. Draft-permissive by construction.
 *
 * `api-conventions.md` §10: a uniformly strict API deadlocks a generator, because one
 * field it cannot produce fails the whole batch. So everything a `DRAFT` row can live
 * without is optional here, and the complete shape is checked once, on the transition to
 * `PUBLISHED`, by `publish-rules.ts` in the API.
 *
 * Not in the package barrel — import `@platform/schema/authoring`. Same reason as
 * `content`: the barrel is on the mobile boot path and Metro does not tree-shake.
 */
/** Required because the column is not nullable — §10's line is what 012 made nullable. */
const slug = z
    .string()
    .min(3)
    .max(80)
    .regex(KEBAB_SLUG, 'lowercase words joined by single hyphens')
    .describe('Stable identifier in URLs. Never reused for different content.');
const title = z.string().min(3).max(120).describe('Shown on the card.');
// 300, not 400: `cardBase` serves `max(300)` and the serializer is global, so a longer
// summary saved here and then threw on every read of the row that carried it.
const summary = z.string().min(3).max(300).describe('One or two sentences.');
export const tagListSchema = z
    .strictObject({
    tags: z.array(z.strictObject({
        slug: tagSlugSchema,
        label: z.string().min(1).max(80).describe('What a human reads.'),
    })),
})
    .describe('GET /authoring/tags');
/**
 * As many entries as there are personas: past that, one is a repeat and not a label.
 * Derived from the enum so a seventh persona does not leave a stale cap behind.
 *
 * The uniqueness check is here rather than left to the composite primary key, which
 * refuses a repeat as a 500 several layers down. `api-conventions.md` §9: a caller's
 * mistake is a 400 that names the field.
 */
const personaLabels = z
    .array(personaLabelSchema)
    .max(personaSchema.options.length)
    .refine((labels) => new Set(labels.map(({ persona }) => persona)).size === labels.length, 'Each persona may appear once. Two grades for one persona is two answers.');
/** The same set without grades, for the screens that change who a lesson is for. */
const personaSet = z
    .array(personaSchema)
    .max(personaSchema.options.length)
    .refine((set) => new Set(set).size === set.length, 'Each persona may appear once.');
/** Created on first use if it does not exist — which is true on a write and nowhere else. */
const tagSlugs = z
    .array(tagSlugSchema)
    .max(12)
    .refine((slugs) => new Set(slugs).size === slugs.length, 'Each tag once.')
    .describe('Tags this is about. A slug not yet in the table is created.');
export const createCollectionSchema = z
    .strictObject({
    slug,
    kind: collectionKindSchema,
    title,
    summary,
    parentId: collectionIdSchema
        .optional()
        .describe('The course a MODULE belongs to. Required for MODULE.'),
    position: z
        .number()
        .int()
        .min(0)
        .max(999)
        .optional()
        .describe('Order within the parent.'),
    subject: subjectSchema.optional(),
    difficulty: difficultySchema.optional(),
    objectName: illustrationObjectSchema.optional(),
    imageUrl: z.url().optional(),
    palette: coursePaletteSchema
        .optional()
        .describe('A COURSE only. Its modules and lessons wear it.'),
    personas: personaLabels.optional(),
})
    .describe('POST /authoring/collections');
export const updateCollectionSchema = createCollectionSchema
    .partial()
    .extend({
    status: contentStatusSchema.optional(),
    palette: coursePaletteSchema
        .nullable()
        .optional()
        .describe('A COURSE only. `null` returns it to its subject family.'),
})
    .describe('PATCH /authoring/collections/:id');
export const createLessonSchema = z
    .strictObject({
    slug,
    title,
    summary,
    format: lessonFormatSchema.optional(),
    estimatedMinutes: z
        .number()
        .int()
        .min(1)
        .max(60)
        .describe('What the card promises. 005 §1 sells five.'),
    difficulty: difficultySchema.optional(),
    objectName: illustrationObjectSchema.optional(),
    verb: verbSchema.optional(),
    personas: personaLabels.optional(),
    tags: tagSlugs.optional(),
    terms: lessonTermsInputSchema.optional(),
    cast: lessonCastSchema.optional(),
    curriculumLessonId: curriculumLessonIdSchema
        .optional()
        .describe('The curriculum Lesson ID it is built from. When present, an upsert matches on it rather than on the slug — CURR-19.'),
})
    .describe('POST /authoring/lessons');
export const updateLessonSchema = createLessonSchema
    .omit({ curriculumLessonId: true })
    .partial()
    .extend({
    status: contentStatusSchema.optional(),
})
    .describe('PATCH /authoring/lessons/:id');
export const createQuestionSchema = z
    .strictObject({
    subject: subjectSchema,
    difficulty: difficultySchema,
    stem: z.string().min(3).max(400).describe('What is being asked.'),
    explanation: z
        .string()
        .trim()
        .min(3)
        .max(1200)
        .describe('Why the answer is what it is, shown once the learner answers.'),
    tags: tagSlugs.optional(),
})
    .describe('POST /authoring/questions');
export const updateQuestionSchema = createQuestionSchema
    .partial()
    .extend({ status: contentStatusSchema.optional() })
    .describe('PATCH /authoring/questions/:id');
export const createSourceSchema = z
    .strictObject({
    type: sourceTypeSchema,
    title: z.string().min(3).max(MAX_SOURCE_TITLE),
    url: z.url().optional(),
    citation: z.string().min(3).max(400).optional(),
})
    .describe('POST /authoring/sources');
/**
 * One line a person typed, as a source row.
 *
 * A URL is a `LINK` and anything else a `TEXT` citation. Here rather than at each caller
 * because there are two — the CSV column the generator reads, and the field a reviewer
 * fills in the console — and a source typed in one place must be the same kind of thing
 * as one supplied in the other.
 *
 * Undefined when there is nothing followable, which is the rule the publish gate applies:
 * `citation` has a minimum for the same reason.
 */
export function sourceFrom(input, title) {
    if (!citable(input))
        return undefined;
    const value = input.trim();
    return value.startsWith('http://') || value.startsWith('https://')
        ? { type: 'LINK', title, url: value }
        : { type: 'TEXT', title, citation: value };
}
/**
 * Whether a line of text can become a source at all — GEN-50.
 *
 * Exported because the generator has to ask *before* it plans. A row is allowed a scene
 * that asserts when it cites something, and the row that is written carries a `sourceId`
 * only if `sourceFrom` accepted the same text. Those were two different tests: a citation
 * of `na` is present, so the planner was cleared to write a CASE, and is unusable, so the
 * scene was written with no source and refused at publish with nothing on screen to fix.
 *
 * One predicate, asked at both ends, is what stops that being possible. It takes the
 * absent case too, because every caller is holding an optional column.
 */
export function citable(input) {
    return input !== undefined && input.trim().length >= 3;
}
/**
 * A scene's own fields. Items and buckets are written with it, because a CHOICE scene
 * with no options is not a partial scene — it is two round trips where one would do, and
 * the generator would have to invent an ordering between them.
 */
export const createSceneItemSchema = z
    .strictObject({
    position: z.number().int().min(1).max(12),
    label: z.string().min(1).max(200),
    imageUrl: z.url().optional(),
    isCorrect: z.boolean().optional(),
    correctPosition: z.number().int().min(1).max(12).optional(),
    correctBucketId: z
        .string()
        .min(1)
        .optional()
        .describe('The position of the bucket this item belongs in, as a string: "1" for the first bucket. Not the bucket label: the buckets have no ids until the same transaction creates them.'),
    correctAmount: z.number().min(0).max(1_000_000).optional(),
    amountTolerance: z.number().min(0).max(1_000_000).optional(),
})
    .describe('One option, card or line the learner touches.');
export const createSceneBucketSchema = z
    .strictObject({
    position: z.number().int().min(1).max(SCENE_BUCKETS_MAX),
    label: z.string().min(1).max(120),
})
    .describe('One target a BUCKET scene sorts into.');
export const createSceneSchema = z
    .strictObject({
    // Narrowed to what an author may write — GEN-91. `REWARD` is the one kind that is
    // not: what a lesson earned is the learner's XP, streak and badges, which
    // `AttemptResult.reward` reports after every lesson from their own projections.
    // Lessons that already hold one still parse — `sceneArms` is untouched.
    kind: authorableKindSchema,
    prompt: z
        .string()
        .min(3)
        .max(400)
        .describe('What the learner reads first.'),
    body: z.string().max(2000).optional(),
    bodyEnabled: z
        .boolean()
        .optional()
        .describe('Whether the subheadline is shown. Absent, on when a body is written.'),
    hint: z.string().max(HINT_MAX).optional(),
    hintEnabled: z.boolean().optional(),
    imageUrl: z.url().optional(),
    objectName: illustrationObjectSchema
        .optional()
        .describe('The illustration a narrative scene shows when it has no image (GEN-120).'),
    questionId: questionIdSchema.optional(),
    sourceId: sourceIdSchema.optional(),
    maxSelections: z.number().int().min(1).max(8).optional(),
    requireSubmit: z.boolean().optional(),
    min: z.number().optional(),
    max: z.number().optional(),
    step: z.number().positive().optional(),
    unit: z.string().max(SLIDER_UNIT_MAX).optional(),
    targetMin: z.number().optional(),
    targetMax: z.number().optional(),
    poolTotal: z.number().positive().optional(),
    counterpartBrief: z.string().max(2000).optional(),
    items: z
        .array(createSceneItemSchema)
        .max(SCENE_ITEMS_MAX.BUCKET)
        .optional(),
    buckets: z.array(createSceneBucketSchema).max(SCENE_BUCKETS_MAX).optional(),
})
    .describe('POST /authoring/scenes');
/**
 * The answer a mechanic grades against, moved on purpose — GEN-49.
 *
 * Separate from `items` rather than folded in, which keeps what the original exclusion was
 * protecting: a label edit still cannot re-key an answer, because labels and keys arrive
 * under different names. Re-keying is now possible and explicit, where it was impossible.
 *
 * It had to become possible. A SLIDER's answer is `targetMin`/`targetMax` — columns
 * `publish-rules.ts` reads and the console could neither show nor correct.
 */
export const rekeySceneItemSchema = z
    .strictObject({
    id: sceneItemIdSchema,
    isCorrect: createSceneItemSchema.shape.isCorrect,
    correctPosition: createSceneItemSchema.shape.correctPosition,
    correctAmount: createSceneItemSchema.shape.correctAmount,
    amountTolerance: createSceneItemSchema.shape.amountTolerance,
})
    .describe('One item, re-keyed.');
/**
 * PATCH /authoring/scenes/:id — a scene's text, and the answer it grades against.
 *
 * Not `createSceneSchema.partial()`, which is the obvious form and the wrong one: that
 * would accept `kind`, and hand editing is not a structural editor (`GEN-09` scope, spec
 * 013 §12). The numeric bounds and the keys were excluded on the same grounds until
 * GEN-49, which found the cost — see `rekeySceneItemSchema` above.
 */
export const updateSceneSchema = z
    .strictObject({
    prompt: createSceneSchema.shape.prompt.optional(),
    body: createSceneSchema.shape.body,
    bodyEnabled: createSceneSchema.shape.bodyEnabled,
    hint: createSceneSchema.shape.hint,
    hintEnabled: createSceneSchema.shape.hintEnabled,
    // On the PATCH and deliberately not on `createSceneSchema` — LESSON-07. A visual is
    // authored onto a scene that already exists, and the create body is round-tripped
    // through the SDK by the console's add and regenerate paths, where a union comes
    // back with `kind: string` and cannot narrow. Nullable where nothing else here is,
    // because it is the only field a reviewer wants to take *off* a scene: absent
    // leaves it alone, `null` clears it. A patch carrying it alone is the visual-only
    // edit scope, and touches no other column.
    visual: sceneVisualSchema.nullable().optional(),
    items: z
        .array(z.strictObject({
        id: sceneItemIdSchema,
        label: createSceneItemSchema.shape.label,
    }))
        .max(12)
        .optional(),
    keys: z.array(rekeySceneItemSchema).max(12).optional(),
    min: createSceneSchema.shape.min,
    max: createSceneSchema.shape.max,
    step: createSceneSchema.shape.step,
    unit: createSceneSchema.shape.unit,
    targetMin: createSceneSchema.shape.targetMin,
    targetMax: createSceneSchema.shape.targetMax,
    poolTotal: createSceneSchema.shape.poolTotal,
    // Editable after creation, unlike the other mechanic columns: it is a judgement
    // about one scene's options rather than a structural fact about the question, and
    // the reviewer who wants it back only discovers that by playing the draft.
    requireSubmit: createSceneSchema.shape.requireSubmit,
    // The one field a CASE or ARTICLE scene cannot publish without, and until now the
    // only way to set it was at creation. A generated scene therefore had no route to a
    // source at all: the reviewer saw "must name its source" and had nothing to answer
    // it with. Spec 013 §17 lists linking a source as a step; this is where a person
    // does it.
    sourceId: sourceIdSchema.optional(),
})
    .describe('PATCH /authoring/scenes/:id');
/**
 * PUT /authoring/lessons/:lessonId/scenes/:sceneId/mechanic — GEN-78.
 *
 * A route of its own rather than a `kind` on the PATCH above, whose docstring gives the
 * reason: hand editing is a text editor, and this rewrites what the scene *is*.
 *
 * The body carries only the target. What survives is a property of the two kinds and not
 * of the request, so a caller cannot ask for the labels to be dropped or the old key
 * kept — `mechanic.ts` decides both, once.
 */
export const switchMechanicSchema = z
    .strictObject({ kind: authorableKindSchema })
    .describe('PUT /authoring/lessons/:lessonId/scenes/:sceneId/mechanic');
/**
 * A payload bound on a placement list, not the scene count. The count is
 * `sceneBoundsRefusal`'s, which names `SCENES_CEILING` and lets an over-long lesson be cut
 * back; a tighter bound here refuses first, with a number nobody can act on — GEN-117.
 */
export const SCENE_ROWS_MAX = 64;
/** Where a scene sits in a lesson, and how long it is given. */
export const placeSceneSchema = z
    .strictObject({
    sceneId: sceneIdSchema,
    position: z.number().int().min(1).max(SCENE_ROWS_MAX),
    timeLimitMs: z.number().int().min(1000).max(600_000).optional(),
})
    .describe('PUT /authoring/lessons/:id/scenes');
/**
 * Which lessons a collection holds, and in what order — spec 013 §5, `012` §6.
 *
 * Mirrors `placeSceneSchema` because it is the same shape of decision one level up: a
 * membership list is one thing, replaced wholesale, rather than a lesson bolted on at a
 * time. Without this the generator writes modules and lessons that nothing joins.
 */
export const placeLessonSchema = z
    .strictObject({
    lessonId: lessonIdSchema,
    position: z.number().int().min(1).max(60),
})
    .describe('Where a lesson sits within a collection.');
export const membershipsSchema = z
    .strictObject({ lessons: z.array(placeLessonSchema).max(60) })
    .describe('PUT /authoring/collections/:id/lessons');
/**
 * Lessons added to what a collection already holds — GEN-37.
 *
 * Ids rather than `placeLessonSchema`'s id-and-position pairs, and that is the whole
 * difference between the two routes. The caller is saying *these belong here too*; where
 * they land is a consequence, and a caller that wants to choose is asking for the `PUT`.
 */
export const addLessonsSchema = z
    .strictObject({ lessonIds: z.array(lessonIdSchema).min(1).max(60) })
    .describe('POST /authoring/collections/:id/lessons');
/**
 * A path's modules, in order — PATH-02. A path is an ordered list of modules and never
 * of lessons, so this is the only membership write a path takes. The array order is the
 * position, and a module appears at most once.
 */
const moduleIds = z
    .array(collectionIdSchema)
    .max(60)
    .refine((ids) => new Set(ids).size === ids.length, 'A module appears once.');
export const placeModulesSchema = z
    .strictObject({ moduleIds })
    .describe('PUT /authoring/collections/:id/modules');
/** Modules added to the end of a path, which a published path still allows. */
export const addModulesSchema = z
    .strictObject({ moduleIds: moduleIds.min(1) })
    .describe('POST /authoring/collections/:id/modules');
export const attachedSchema = z
    .strictObject({ attached: z.number().int().min(0) })
    .describe('How many lessons the collection now holds.');
/**
 * What every authoring write returns: the id, and what happened to the row.
 *
 * `state` exists for the generator's re-run. `accepted` means a human had already taken
 * the row out of `DRAFT`, so nothing was overwritten — a proposal whose acceptance is
 * durable is the only kind worth reviewing.
 */
export const writtenSchema = z
    .strictObject({
    id: z.string().min(1),
    state: z
        .enum(['created', 'updated', 'accepted'])
        .optional()
        .describe('Absent on routes that only ever create.'),
})
    .describe('The row that was written.');
/** POST /authoring/lessons/:id/object — what the re-roll landed on. */
export const rerolledObjectSchema = z
    .strictObject({
    objectName: illustrationObjectSchema.nullable(),
})
    .describe('The illustration object now on the lesson.');
export const placementsSchema = z
    .strictObject({
    scenes: z.array(placeSceneSchema).min(1).max(SCENE_ROWS_MAX),
})
    .describe('PUT /authoring/lessons/:id/scenes');
export const publishReadinessSchema = z
    .strictObject({ issues: z.array(problemDetailSchema) })
    .describe('What stops a lesson publishing. Empty means it would.');
export const placedSchema = z
    .strictObject({ placed: z.number().int().min(0) })
    .describe('How many scenes the lesson now has.');
/**
 * One rule, and the card it belongs to.
 *
 * A subtree's issues are pooled from the module, its course and every lesson, and `path`
 * alone cannot say which: `objectName` read the same whether the module or one of six
 * lessons was missing art, so nobody could tell where to fix it.
 */
export const readinessIssueSchema = problemDetailSchema.extend({
    item: z.strictObject({
        kind: z.enum([...collectionKindSchema.options, 'LESSON']),
        id: z.string().min(1),
        title: z.string(),
    }),
});
/**
 * The DRAFT course a module's publish takes live with it. The cascade is GEN-30's: Explore
 * browses courses, so a live module under a draft one has no way in. Named so the console
 * can say so before the click, and after it.
 */
const draftCourseSchema = z
    .strictObject({ id: z.string(), title: z.string() })
    .describe('The DRAFT course this module belongs to, which publishing it publishes too. Absent when there is none.');
/** A lesson in a collection's publish, and whether learners see it for the first time. */
const publishingLessonSchema = z.strictObject({
    id: z.string(),
    slug: z.string(),
    title: z.string(),
    change: z
        .enum(['NEW', 'UPDATE'])
        .describe('NEW: a draft going live for the first time. UPDATE: a published lesson whose edits replace what learners are served.'),
});
/**
 * What stops a whole collection publishing — spec 013 §6.2, §8.3.
 *
 * `issues` is the gate and `blockers` is advice. They are separate fields because they
 * are different kinds of thing: a validator issue is a rule the content breaks and no
 * confirmation gets past it, while a reviewer's `BLOCKER` is an opinion a person may
 * overrule. Collapsing them into one list would make the console lie about which.
 */
export const subtreeReadinessSchema = z
    .strictObject({
    status: contentStatusSchema.describe('The collection as it stands. Publishing is the console’s one irreversible action and nothing said whether it had happened — GEN-24.'),
    issues: z.array(readinessIssueSchema),
    openBlockers: z
        .number()
        .int()
        .min(0)
        .describe('How many `blockers` there are. Kept so older clients still read it; `blockers` says which.'),
    blockers: z
        .array(readinessIssueSchema)
        .describe('Open BLOCKER findings across the subtree, each on the card it names — `path` is `scenes[n]` for one on a scene. Advice, not a gate.'),
    stopped: z
        .array(readinessIssueSchema)
        .describe('Draft lessons whose newest generation row stopped part way, FAILED or PARTIAL, with the error it stopped on. What it wrote may be unfinished, so publishing asks for the same acknowledgement as `blockers`.'),
    review: z
        .enum(['NONE', 'RUNNING', 'DONE', 'NOT_RUN'])
        .describe('Where the newest generation review of each module stands, the least finished winning. RUNNING has filed no blockers yet, so a zero beside it means nothing; NOT_RUN is a review its job stopped before running. NONE: no job generated it.'),
    draftLessons: z
        .number()
        .int()
        .min(0)
        .describe('Lessons in the subtree still in DRAFT. A live collection stays live while one is, and learners do not see it.'),
    going: z
        .array(publishingLessonSchema)
        .describe('Every approved draft that publishing takes live together — CURR-46. Send their ids back as `lessonIds` to confirm this set.'),
    held: z
        .array(publishingLessonSchema)
        .describe('Drafts that stay as they are because they are not approved. A published one keeps serving what it served.'),
    course: draftCourseSchema.optional(),
})
    .describe('GET /authoring/collections/:id/publish-readiness');
export const publishCollectionSchema = z
    .strictObject({
    acknowledgeBlockers: z
        .boolean()
        .describe('Proceed with open BLOCKER findings and `stopped` lessons. The second confirmation — §8.3 keeps the reviewer advisory, and this is what makes overruling it deliberate.'),
    lessonIds: z
        .array(lessonIdSchema)
        .max(500)
        .optional()
        .describe('The `going` set the console showed. Refused with 409 when it no longer matches, so what was confirmed is what goes live.'),
})
    .describe('POST /authoring/collections/:id/publish');
/**
 * A collection's lessons, for the console — spec 013 §11.
 *
 * The authoring side needs this and the read side cannot give it: `CatalogReadService`
 * pins `status = PUBLISHED` on every query, and a reviewer's whole job is the drafts. So
 * a module's screen had a publish button and no way to see what it was about to publish.
 */
export const collectionLessonSchema = z
    .strictObject({
    id: lessonIdSchema,
    slug,
    title,
    status: contentStatusSchema,
    position: z.number().int(),
    scenes: z.number().int().min(0),
})
    .describe('One lesson in a collection, as the console lists it.');
/**
 * The import that last outlined this module, and how far its lessons have got.
 *
 * A job row links to its module as soon as the outline lands, minutes before any lesson
 * does, so the module page needs to say the lessons are coming and where from.
 */
export const moduleGenerationSchema = z
    .strictObject({
    jobId: generationJobIdSchema,
    live: z
        .boolean()
        .describe('Whether the job can still write lessons into this module.'),
    lessons: rowTallySchema.describe('This module’s lessons in that job, by status.'),
})
    .describe('The latest import writing this module’s lessons.');
export const collectionLessonsSchema = z
    .strictObject({
    lessons: z.array(collectionLessonSchema),
    generation: moduleGenerationSchema.optional(),
})
    .describe('GET /authoring/collections/:id/lessons');
/** One module in a path, with its lessons in the module's own order — PATH-02. */
export const pathModuleSchema = z
    .strictObject({
    id: collectionIdSchema,
    slug,
    title,
    status: contentStatusSchema,
    position: z.number().int().min(1),
    courseTitle: title.optional(),
    lessons: z.array(collectionLessonSchema),
})
    .describe('One module in a path, as the console lists it.');
export const pathModulesSchema = z
    .strictObject({ modules: z.array(pathModuleSchema) })
    .describe('GET /authoring/collections/:id/modules');
/** The `path` filter's value for modules in no path at all — not a slug, so never one. */
export const NO_PATH = '_none';
/**
 * The order the collection list comes back in. `PATHS_*` sorts by a module's first path
 * title, alphabetically, with modules in no path last either way.
 */
export const collectionSortSchema = z.enum([
    'UPDATED_DESC',
    'PATHS_ASC',
    'PATHS_DESC',
]);
/**
 * What both browse endpoints ask the same way — GEN-61.
 *
 * Spread rather than written twice: search is matched against the same two columns with
 * the same cap on both, and a page is 25 of at most 100 on both. Two copies drift on the
 * caps first, and nothing catches it.
 */
const browseQuery = {
    q: z
        .string()
        .trim()
        .min(1)
        .max(120)
        .optional()
        .describe('Matched against the title and the slug, case-insensitively.'),
    limit: z.coerce.number().int().min(1).max(100).default(25),
    offset: z.coerce.number().int().min(0).default(0),
};
/**
 * Browsing what the console has made — GEN-58.
 *
 * The catalog cannot answer this and never will: `CatalogReadService` pins
 * `status = PUBLISHED` on every query it makes, and everything the console is for is a
 * draft. So the admin could create a course and then had no way to find it — a course was
 * reachable only from the job that produced it, and only while you remembered which.
 *
 * Filtered on the server rather than in the client. A filter applied after a full fetch
 * is a filter that stops working at the size the fetch stops being free, and the point of
 * this screen is the library nobody can hold in their head.
 */
export const browseCollectionsSchema = z
    .strictObject({
    /** One collection by id — what a detail screen asks for. */
    id: collectionIdSchema.optional(),
    /** The children of one course, which is the other half of a detail screen. */
    parentId: collectionIdSchema.optional(),
    /**
     * The modules one run produced — GEN-60. `GenerationRow.collectionId` is the link,
     * and it is the question the modules list is most often opened to answer: *what came
     * out of last night's job*. Only a MODULE has one, so this narrows to modules by
     * construction rather than by also passing `kind`.
     */
    jobId: generationJobIdSchema.optional(),
    /** Modules in this path, by its slug — or in no path, with `NO_PATH`. PATH-02. */
    path: z.union([z.literal(NO_PATH), slug]).optional(),
    /** Paths holding this module, by its slug. PATH-02. */
    module: slug.optional(),
    kind: collectionKindSchema.optional(),
    subject: subjectSchema.optional(),
    status: contentStatusSchema.optional(),
    sort: collectionSortSchema.default('UPDATED_DESC'),
    ...browseQuery,
})
    .describe('GET /authoring/collections');
/**
 * One row of the list.
 *
 * `lessons` and `modules` are counts rather than the rows themselves: the question this
 * screen answers is *which course*, and a course carrying its whole subtree would make
 * the list the size of the catalog.
 *
 * `summary` and `objectName` are here for the detail screens, which read their one row
 * through this endpoint — and they are the last two of those. What a third one costs, and
 * why `parentTitle` is not one: `docs/decisions/collection-row-serves-the-detail-screen.md`.
 */
export const browsedCollectionSchema = z
    .strictObject({
    id: collectionIdSchema,
    slug,
    title,
    summary,
    kind: collectionKindSchema,
    status: contentStatusSchema,
    subject: subjectSchema.optional(),
    difficulty: difficultySchema.optional(),
    objectName: illustrationObjectSchema.optional(),
    parentId: collectionIdSchema.optional(),
    /** Denormalised for the modules list — `collection-row-serves-the-detail-screen.md`. */
    parentTitle: title.optional(),
    /** A path's count is its modules' lessons, drafts included. */
    lessons: z.number().int().min(0),
    /** A course's children, or a path's modules. */
    modules: z.number().int().min(0),
    /** The paths a module sits in, by title, and where — empty for a course or a path. */
    paths: z.array(z.strictObject({
        id: collectionIdSchema,
        slug,
        title,
        status: contentStatusSchema,
        position: z.number().int().min(1),
        of: z.number().int().min(1),
    })),
    updatedAt: z.iso.datetime(),
})
    .describe('One collection, as the console lists it.');
/**
 * One collection, as its detail screen edits it — VIS-18.
 *
 * The by-id read `collection-row-serves-the-detail-screen.md` asked for once a detail
 * form wanted a field the list does not: the browsed row, plus the palette and the
 * lessons whose ownership contradicts it.
 */
export const authoringCollectionSchema = browsedCollectionSchema
    .extend({
    palette: coursePaletteSchema.optional(),
    sharedLessons: z
        .array(z.strictObject({
        id: lessonIdSchema,
        slug,
        title,
        otherCourses: z.array(title).min(1),
    }))
        .describe("A course's lessons that also sit in another course's module. Each should have one, so it wears one course's colours; empty on a module or a path."),
})
    .describe('GET /authoring/collections/:id');
export const browsedCollectionsSchema = z
    .strictObject({
    collections: z.array(browsedCollectionSchema),
    /** The whole matching set, so the screen can say what it is showing part of. */
    total: z.number().int().min(0),
})
    .describe('A page of collections.');
/**
 * Ready or not, as the gate decides it — GEN-61.
 *
 * Two named states rather than a boolean in a query string. `?readiness=BLOCKED` says
 * what it filters for where `?ready=false` needs the reader to know which way round the
 * question was asked.
 */
export const readinessSchema = z.enum(['READY', 'BLOCKED']);
/**
 * The order the lesson list comes back in — ADMIN-16.
 *
 * `CREATED_DESC` by default so a lesson just made is on the first page whatever its status;
 * none of these filter, so a draft and a published lesson sort together. `PUBLISHED_DESC`
 * puts lessons never published last.
 */
export const lessonSortSchema = z.enum([
    'CREATED_DESC',
    'CREATED_ASC',
    'PUBLISHED_DESC',
    'TITLE_ASC',
    'TITLE_DESC',
    'UPDATED_DESC',
    'HELPFUL_ASC',
    'HELPFUL_DESC',
    'NOT_HELPFUL_DESC',
    'FEEDBACK_DESC',
    'FEEDBACK_RECENT',
]);
/** The cap on a lesson's research notes, a hard one — the API refuses past it. */
export const NOTES_WORD_LIMIT = 150;
/** The cap on a one-line note: a source's, or why a lesson needs changes. */
export const LINE_WORD_LIMIT = 30;
export const LINE_MAX = 400;
const line = z
    .string()
    .trim()
    .min(1)
    .max(LINE_MAX)
    .refine((text) => wordsIn(text) <= LINE_WORD_LIMIT, {
    message: `Keep it to ${String(LINE_WORD_LIMIT)} words.`,
});
export const lessonReviewStateSchema = z
    .strictObject({
    status: reviewStatusSchema,
    reason: z
        .string()
        .optional()
        .describe('Why it needs changes. Present only with NEEDS_CHANGES.'),
    handSet: z
        .boolean()
        .describe('Marked by hand, so removing every source does not clear the status.'),
    reviewer: z
        .string()
        .optional()
        .describe('The email of whoever last approved it or asked for changes.'),
    reviewedAt: z.iso.datetime().optional(),
})
    .describe('Where the human review of a lesson stands.');
export const lessonSourceSchema = createSourceSchema
    .extend({
    id: sourceIdSchema,
    url: z.string().optional(),
    citation: z.string().optional(),
    note: z
        .string()
        .optional()
        .describe('What it backs in this lesson. Another lesson has its own.'),
})
    .describe('A shared source, as one lesson cites it.');
export const lessonResearchSchema = z
    .strictObject({
    sources: z.array(lessonSourceSchema),
    notes: z.string(),
    review: lessonReviewStateSchema
        .nullable()
        .describe('Null until a source is attached or it is marked by hand.'),
})
    .describe('What a lesson is based on, for admins only. Never served to a learner.');
/**
 * Attach a shared source to a lesson. Attaching starts review, and sends an approved
 * lesson back to it: a new source is a new claim nobody has checked.
 */
export const attachSourceSchema = z
    .strictObject({ sourceId: sourceIdSchema, note: line.optional() })
    .describe('POST /authoring/lessons/:id/sources');
/** A note edit never resets review — only a change to the sources does. */
export const updateSourceNoteSchema = z
    .strictObject({
    note: line.nullable().describe('`null` removes it.'),
})
    .describe('PATCH /authoring/lessons/:id/sources/:sourceId');
/**
 * The word cap is checked by the API rather than here, so a refusal can be counted:
 * how often admins hit it decides whether it moves.
 */
export const updateNotesSchema = z
    .strictObject({ notes: z.string().max(4000) })
    .describe('PUT /authoring/lessons/:id/notes');
export const setReviewSchema = z
    .strictObject({
    status: reviewStatusSchema
        .nullable()
        .describe('NEEDS_REVIEW by hand is the major-fact-change mark. `null` clears it.'),
    reason: line
        .optional()
        .describe('Required with NEEDS_CHANGES, and only with it.'),
})
    .check((ctx) => {
    const needsReason = ctx.value.status === 'NEEDS_CHANGES';
    if (needsReason !== (ctx.value.reason !== undefined))
        ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: ['reason'],
            message: needsReason
                ? 'Say what needs changing.'
                : 'A reason goes only with NEEDS_CHANGES.',
        });
})
    .describe('PUT /authoring/lessons/:id/review');
/** What the module filter sends for the lessons no module holds yet. */
export const NO_MODULE = 'NONE';
/**
 * Every lesson the console has, filtered — GEN-61.
 *
 * The collections browse one level down, and it could not be the same endpoint: a lesson
 * is not a collection, it belongs to several of them, and the two filters that matter
 * here — `difficulty` and `ready` — have no meaning for a bag of lessons.
 *
 * `subject` filters through membership, because a lesson has no subject column. Topic is
 * a claim about what a thing teaches, and the model puts that on the collection; a lesson
 * in two courses of different topics matches either.
 */
export const browseLessonsSchema = z
    .strictObject({
    /** The lessons in the modules of one course. */
    courseId: collectionIdSchema.optional(),
    moduleId: z
        .union([z.literal(NO_MODULE), collectionIdSchema])
        .optional()
        .describe(`The lessons of one module, or \`${NO_MODULE}\` for those in no module yet.`),
    lessonId: lessonIdSchema.optional(),
    subject: subjectSchema
        .optional()
        .describe('Matched through the collections the lesson belongs to.'),
    difficulty: difficultySchema.optional(),
    status: contentStatusSchema.optional(),
    review: reviewStatusSchema.optional(),
    readiness: readinessSchema
        .optional()
        .describe('What the publish gate says. `BLOCKED` is the useful one: it is the list of what still needs work.'),
    persona: personaSchema
        .optional()
        .describe('Lessons carrying this persona, at any fit.'),
    tag: tagSlugSchema.optional(),
    feedbackDays: z.coerce
        .number()
        .int()
        .min(1)
        .max(365)
        .optional()
        .describe('Only lessons answered in the last this many days, and only those answers in their feedback — rated at any count.'),
    sort: lessonSortSchema.default('CREATED_DESC'),
    ...browseQuery,
})
    .describe('GET /authoring/lessons');
/** A module, course or path, as a lesson's row names it — ADMIN-18. */
export const lessonPlaceSchema = z
    .strictObject({ id: collectionIdSchema, title })
    .describe('One collection a lesson sits under.');
/**
 * One row of the lesson list.
 *
 * `blockers` is a count and not the issues themselves. The question the list answers is
 * *which lesson*, and five lessons carrying every problem detail of eight scenes each is
 * the review workspace served as a table. The workspace is one click away and names them.
 */
export const browsedLessonSchema = z
    .strictObject({
    id: lessonIdSchema,
    slug,
    title,
    status: contentStatusSchema,
    difficulty: difficultySchema.optional(),
    scenes: z.number().int().min(0),
    blockers: z
        .number()
        .int()
        .min(0)
        .describe('How many rules the publish gate says it breaks. 0 would publish.'),
    personas: z.array(personaSchema),
    tags: z.array(tagSlugSchema),
    /** Absent while the lesson waits to be placed. One, because a lesson sits in one. */
    module: lessonPlaceSchema.optional(),
    course: lessonPlaceSchema.optional(),
    paths: z.array(lessonPlaceSchema),
    createdAt: z.iso.datetime(),
    /** When it last became PUBLISHED. Absent if it never has, or did before ADMIN-16. */
    publishedAt: z.iso.datetime().optional(),
    updatedAt: z.iso.datetime(),
    version: z
        .number()
        .int()
        .min(1)
        .describe('Goes up each time the lesson is published again.'),
    feedback: feedbackTallySchema.describe('What learners said of the live version.'),
    review: lessonReviewStateSchema
        .pick({ status: true, reason: true })
        .optional()
        .describe('Absent until a source is attached or it is marked by hand.'),
})
    .describe('One lesson, as the console lists it.');
export const browsedLessonsSchema = z
    .strictObject({
    lessons: z.array(browsedLessonSchema),
    /** The whole matching set, so the screen can say what it is showing part of. */
    total: z.number().int().min(0),
})
    .describe('A page of lessons.');
/**
 * Archive several lessons at once — GEN-61.
 *
 * Archive and not a general `status`, because the other two transitions are not bulk acts:
 * `PUBLISHED` has a gate that must refuse per lesson and name why, and moving back to
 * `DRAFT` is the repair path for one broken thing. This is the one that is safe to do to
 * twenty rows a reviewer just looked at.
 *
 * One request rather than the console looping `PATCH`: a loop that fails on the seventh
 * leaves six archived, nothing to roll it back, and one entry in the action log for the
 * whole thing.
 */
export const archiveLessonsSchema = z
    .strictObject({ lessonIds: z.array(lessonIdSchema).min(1).max(100) })
    .describe('POST /authoring/lessons/archive');
export const archivedSchema = z
    .strictObject({ archived: z.number().int().min(0) })
    .describe('How many lessons moved to ARCHIVED.');
/**
 * Set the level or the personas on several lessons at once — GEN-70.
 *
 * The two axes a reviewer changes without opening a lesson, and neither is a judgement you
 * have to read the scenes to make. Both were reachable only through the lesson's own
 * PATCH, so relabelling a module meant opening twenty workspaces.
 *
 * One endpoint rather than two, because a reviewer sorting a module's audience sets both
 * in the same pass. Not a general `status` — `archiveLessonsSchema` above says why that
 * stays its own route. Not the text either: a title is written by somebody reading it.
 */
export const relabelLessonsSchema = z
    .strictObject({
    lessonIds: z.array(lessonIdSchema).min(1).max(100),
    difficulty: difficultySchema.optional(),
    // Bare personas, not the graded labels the lesson PATCH takes. The list shows who a
    // lesson is for and not how well it fits them, so a grade cannot be set from here —
    // and one sent through would be a grade nobody chose. `mergedPersonas` keeps the ones
    // already on each lesson, per lesson, which is why the merge is the API's.
    personas: personaSet.optional(),
})
    .check((ctx) => {
    if (ctx.value.difficulty === undefined && ctx.value.personas === undefined)
        ctx.issues.push({
            code: 'custom',
            input: ctx.value,
            path: ['difficulty'],
            message: 'Send a level, a persona set, or both — this changes nothing.',
        });
})
    .describe('POST /authoring/lessons/labels');
export const relabelledSchema = z
    .strictObject({ relabelled: z.number().int().min(0) })
    .describe('How many lessons the labels were written to.');
export const publishedSchema = z
    .strictObject({
    collections: z.number().int().min(0),
    lessons: z.number().int().min(0),
    course: draftCourseSchema.optional(),
})
    .describe('How many rows the publish moved.');
/** POST /authoring/collections/:id/fill — ADMIN-29. */
export const courseFilledSchema = z
    .strictObject({
    id: z.string().min(1),
    filled: z
        .array(z.enum(['title', 'summary', 'subject', 'difficulty', 'objectName']))
        .describe('The fields the fill wrote. Empty when nothing needed it.'),
})
    .describe('A draft course, filled in so it can publish.');
//# sourceMappingURL=authoring.js.map